import { Order } from '../types/order';

/**
 * CTA SCENARIOS TESTING GUIDE
 * ============================
 * 
 * The last 5 orders in this file (IDs: scenario-a through scenario-e) test all 5 CTA scenarios.
 * 
 * To test each scenario:
 * 1. Go to ACCOUNT tab → Configurações do protótipo
 * 2. Set the coin balance using the preset buttons or custom input
 * 3. Return to LOJA tab and scroll to the bottom
 * 4. Open the test orders and verify the CTAs
 * 
 * NEW CLEAN ARCHITECTURE (Inspired by Airbnb/Slack/Notion):
 * - Vertical stacking: Action → Price → Status (when needed)
 * - Concise labels: "Exclusivo" instead of "Liberar como exclusivo"
 * - Clear hierarchy: 16px (action/price) → 12px (status)
 * - Center-aligned, mobile-friendly, no text wrapping
 * 
 * Scenario A (scenario-a): Set balance = 500
 *   - Expected: Blue button, 1 line: "Liberar por 150 moedas"
 *   - Condition: Already unlocked (2 pros) + user has enough coins
 * 
 * Scenario B (scenario-b): Set balance = 500
 *   - Expected: Green button (2 lines: "👑 Exclusivo" + "250 moedas") + White button (1 line)
 *   - Condition: Not unlocked (0 pros) + user has enough for both
 * 
 * Scenario C (scenario-c): Set balance = 20
 *   - Expected: Blue button, 2 lines: "Liberar por 150 moedas" + "Completar saldo"
 *   - Condition: Already unlocked (3 pros) + user does NOT have enough coins
 * 
 * Scenario D (scenario-d): Set balance = 200 (DEFAULT)
 *   - Expected: Green button (3 lines with status) + White button (1 line, no status)
 *   - Condition: Not unlocked (0 pros) + user has enough for normal but NOT exclusive
 * 
 * Scenario E (scenario-e): Set balance = 10
 *   - Expected: Green button (3 lines) + White button (2 lines), both with "Completar saldo"
 *   - Condition: Not unlocked (0 pros) + user does NOT have enough for either option
 * 
 * Typography: Action/Price = 16px semibold, Status = 12px medium with 75% opacity
 */

export const mockOrders: Order[] = [
  // ============================================
  // SECTION 1: PEDIDOS EM DESTAQUE (Highlighted)
  // 3 pedidos
  // ============================================
  {
    id: 'h1',
    customerName: 'Roberto S.',
    customerInitials: 'RS',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Preciso instalar tomadas extras na sala e quarto, além de trocar 3 interruptores que estão com mau contato.',
    createdAt: new Date(Date.now() - 1000 * 60 * 5), // 5 min ago
    distance: 2.1,
    neighbourhood: 'Vila Mariana',
    unlockedCount: 0,
    maxUnlocks: 5,
    isUrgent: true,
    isHighlighted: true,
    isRecommended: false,
    isViewed: false,
    coinCost: 200,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
      'https://images.unsplash.com/photo-1513828583688-c52646db42da?w=1080',
    ],
    suggestedPrice: 450,
    estimatedDuration: '2-3 horas',
    location: {
      city: 'Vila Mariana, São Paulo',
      state: 'SP',
      zipCode: '04101-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 3 dias',
      },
      {
        question: 'Quantas tomadas precisa instalar?',
        answer: '4 tomadas no total',
      },
      {
        question: 'O disjuntor suporta a carga adicional?',
        answer: 'Não sei, preciso que o profissional avalie',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim, já contratei eletricista antes',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Prefiro período da tarde',
      },
    ],
    clientInfo: {
      memberSince: 'janeiro de 2024',
      completedRequests: 8,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h2',
    customerName: 'Mariana L.',
    customerInitials: 'ML',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 12), // 12 min ago
    distance: 3.5,
    neighbourhood: 'Pinheiros',
    unlockedCount: 1,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: true,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 280,
    estimatedDuration: '1-2 horas',
    location: {
      city: 'Pinheiros, São Paulo',
      state: 'SP',
      zipCode: '05422-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 7 dias',
      },
      {
        question: 'Qual é o problema?',
        answer: 'Torneira da pia da cozinha está pingando',
      },
      {
        question: 'Já tentou algum reparo?',
        answer: 'Não, prefiro deixar com profissional',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim, já contratei encanador antes',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Tanto faz, trabalho de casa',
      },
    ],
    clientInfo: {
      memberSince: 'agosto de 2023',
      completedRequests: 12,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h3',
    customerName: 'Carlos E.',
    customerInitials: 'CE',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: 'iPhone 12 com tela trincada e bateria viciada. Precisa trocar tela original e avaliar se vale a pena trocar a bateria também.',
    createdAt: new Date(Date.now() - 1000 * 60 * 25), // 25 min ago
    distance: 1.8,
    neighbourhood: 'Moema',
    unlockedCount: 2,
    maxUnlocks: 5,
    isUrgent: true,
    isHighlighted: true,
    isRecommended: false,
    isViewed: false,
    coinCost: 300,
    attachedImages: 4,
    photos: [
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
      'https://images.unsplash.com/photo-1585060544812-6b45742d762f?w=1080',
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1080',
    ],
    suggestedPrice: 850,
    estimatedDuration: '1-2 dias',
    location: {
      city: 'Moema, São Paulo',
      state: 'SP',
      zipCode: '04524-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Urgente, preciso do celular funcionando',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'iPhone 12, 128GB, azul',
      },
      {
        question: 'Prefere peças originais ou compatíveis?',
        answer: 'Prefiro original se o preço for razoável',
      },
      {
        question: 'O aparelho está com garantia?',
        answer: 'Não, já passou da garantia',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Prefiro manhã entre 9h e 12h',
      },
    ],
    clientInfo: {
      memberSince: 'março de 2022',
      completedRequests: 24,
      hasWhatsApp: true,
    },
  },

  // ============================================
  // SECTION 2: PARA VOCÊ (Recommended)
  // 17 pedidos (10 originais + 7 movidos da seção destacada)
  // ============================================
  {
    id: 'h4',
    customerName: 'Juliana P.',
    customerInitials: 'JP',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Instalação de ventilador de teto em 2 quartos e chuveiro elétrico no banheiro.',
    createdAt: new Date(Date.now() - 1000 * 60 * 40), // 40 min ago
    distance: 4.2,
    neighbourhood: 'Jardins',
    unlockedCount: 3,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 250,
    attachedImages: 3,
    photos: [
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1080',
      'https://images.unsplash.com/photo-1581092918484-8313de5e6214?w=1080',
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
    ],
    suggestedPrice: 520,
    estimatedDuration: '3-4 horas',
    location: {
      city: 'Jardins, São Paulo',
      state: 'SP',
      zipCode: '01401-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'No próximo mês',
      },
      {
        question: 'Os equipamentos já foram comprados?',
        answer: 'Sim, já tenho os ventiladores e o chuveiro',
      },
      {
        question: 'A fiação está preparada?',
        answer: 'Não sei, preciso que avalie',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não, primeira vez',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Só consigo nos finais de semana',
      },
    ],
    clientInfo: {
      memberSince: 'novembro de 2023',
      completedRequests: 5,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h5',
    customerName: 'Fernando A.',
    customerInitials: 'FA',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 55), // 55 min ago
    distance: 5.7,
    neighbourhood: 'Brooklin',
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: true,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 400,
    attachedImages: 1,
    photos: [
      'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1080',
    ],
    suggestedPrice: 350,
    estimatedDuration: '2-3 horas',
    location: {
      city: 'Brooklin, São Paulo',
      state: 'SP',
      zipCode: '04571-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Urgente, nos próximos 2 dias',
      },
      {
        question: 'Qual é o problema?',
        answer: 'Vazamento embaixo da pia do banheiro',
      },
      {
        question: 'O vazamento é constante?',
        answer: 'Sim, está vazando água o tempo todo',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim, já contratei encanador',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Preciso que seja depois das 14h',
      },
    ],
    clientInfo: {
      memberSince: 'fevereiro de 2024',
      completedRequests: 3,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h6',
    customerName: 'Patricia N.',
    customerInitials: 'PN',
    category: 'Diarista',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 75), // 1h15min ago
    distance: 3.9,
    neighbourhood: 'Perdizes',
    unlockedCount: 2,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 350,
    attachedImages: 5,
    photos: [
      'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?w=1080',
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
      'https://images.unsplash.com/photo-1585060544812-6b45742d762f?w=1080',
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1080',
    ],
    suggestedPrice: 320,
    estimatedDuration: '1 dia',
    location: {
      city: 'Perdizes, São Paulo',
      state: 'SP',
      zipCode: '05015-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 7 dias',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'Samsung Galaxy S21',
      },
      {
        question: 'O problema começou de repente?',
        answer: 'Sim, ontem parou de carregar completamente',
      },
      {
        question: 'Já testou com outros cabos?',
        answer: 'Sim, testei 3 cabos diferentes',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Flexível, trabalho remoto',
      },
    ],
    clientInfo: {
      memberSince: 'setembro de 2023',
      completedRequests: 16,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h7',
    customerName: 'Diego M.',
    customerInitials: 'DM',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Disjuntor desarmando com frequência. Preciso avaliar toda a instalação elétrica do apartamento.',
    createdAt: new Date(Date.now() - 1000 * 60 * 95), // 1h35 ago
    distance: 7.1,
    neighbourhood: 'Tatuapé',
    unlockedCount: 2,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 200,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1080',
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
    ],
    suggestedPrice: 650,
    estimatedDuration: '4-5 horas',
    location: {
      city: 'Tatuapé, São Paulo',
      state: 'SP',
      zipCode: '03310-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Em 10 dias',
      },
      {
        question: 'Com que frequência o disjuntor desarma?',
        answer: '2-3 vezes por dia, especialmente quando uso o micro-ondas',
      },
      {
        question: 'Quando foi feita a última revisão elétrica?',
        answer: 'Nunca foi feita, apartamento tem 15 anos',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não, primeira vez',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Qualquer horário, estou disponível',
      },
    ],
    clientInfo: {
      memberSince: 'maio de 2024',
      completedRequests: 2,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h8',
    customerName: 'Camila T.',
    customerInitials: 'CT',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 120), // 2h ago
    distance: 3.9,
    neighbourhood: 'Itaim Bibi',
    unlockedCount: 4,
    maxUnlocks: 5,
    isUrgent: true,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 150,
    attachedImages: 3,
    photos: [
      'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1080',
      'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1080',
      'https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=1080',
    ],
    suggestedPrice: 220,
    estimatedDuration: '1-2 horas',
    location: {
      city: 'Itaim Bibi, São Paulo',
      state: 'SP',
      zipCode: '04538-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Esta semana',
      },
      {
        question: 'Qual é o problema?',
        answer: 'Chuveiro com pouca pressão de água',
      },
      {
        question: 'Afeta só o chuveiro ou outras saídas?',
        answer: 'Só o chuveiro, as torneiras estão normais',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim, já contratei antes',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Prefiro de manhã',
      },
    ],
    clientInfo: {
      memberSince: 'dezembro de 2023',
      completedRequests: 9,
      hasWhatsApp: false,
    },
  },
  {
    id: 'h9',
    customerName: 'Bruno K.',
    customerInitials: 'BK',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: 'Xiaomi Redmi Note 11 desligando sozinho e travando. Preciso formatar e instalar ROM limpa.',
    createdAt: new Date(Date.now() - 1000 * 60 * 150), // 2h30 ago
    distance: 8.5,
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 300,
    attachedImages: 4,
    photos: [
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
      'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?w=1080',
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1080',
    ],
    suggestedPrice: 180,
    estimatedDuration: '2-3 horas',
    location: {
      city: 'Santana, São Paulo',
      state: 'SP',
      zipCode: '02011-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 5 dias',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'Xiaomi Redmi Note 11',
      },
      {
        question: 'Fez backup dos dados?',
        answer: 'Sim, já fiz backup de tudo importante',
      },
      {
        question: 'O problema começou após alguma atualização?',
        answer: 'Acho que sim, começou depois de atualizar',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Tarde ou final de semana',
      },
    ],
    clientInfo: {
      memberSince: 'julho de 2023',
      completedRequests: 11,
      hasWhatsApp: true,
    },
  },
  {
    id: 'h10',
    customerName: 'Vanessa R.',
    customerInitials: 'VR',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Instalação de spot lights no teto da sala (8 unidades) e dimmer para controle de intensidade.',
    createdAt: new Date(Date.now() - 1000 * 60 * 180), // 3h ago
    distance: 4.6,
    unlockedCount: 1,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 450,
    attachedImages: 5,
    photos: [
      'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1080',
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1080',
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
      'https://images.unsplash.com/photo-1513828583688-c52646db42da?w=1080',
      'https://images.unsplash.com/photo-1581092918484-8313de5e6214?w=1080',
    ],
    suggestedPrice: 780,
    estimatedDuration: '4-5 horas',
    location: {
      city: 'Vila Madalena, São Paulo',
      state: 'SP',
      zipCode: '05435-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 20 dias',
      },
      {
        question: 'Os spots e o dimmer já foram comprados?',
        answer: 'Não, preciso de orientação sobre qual comprar',
      },
      {
        question: 'O teto é de gesso?',
        answer: 'Sim, gesso com rebaixamento',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim, já fiz iluminação antes',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Flexível',
      },
    ],
    clientInfo: {
      memberSince: 'outubro de 2022',
      completedRequests: 19,
      hasWhatsApp: true,
    },
  },
  {
    id: 'r1',
    customerName: 'André B.',
    customerInitials: 'AB',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: 'Instalação de filtro de água embaixo da pia da cozinha e torneira nova.',
    createdAt: new Date(Date.now() - 1000 * 60 * 8), // 8 min ago
    distance: 1.2,
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: true,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 250,
    attachedImages: 1,
    photos: [
      'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1080',
    ],
    suggestedPrice: 320,
    estimatedDuration: '2-3 horas',
    location: {
      city: 'Mooca, São Paulo',
      state: 'SP',
      zipCode: '03104-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 5 dias',
      },
      {
        question: 'Já comprou o filtro e a torneira?',
        answer: 'Sim, já tenho tudo',
      },
      {
        question: 'Existe espaço suficiente embaixo da pia?',
        answer: 'Sim, tem bastante espaço',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não, primeira vez',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Prefiro de manhã',
      },
    ],
    clientInfo: {
      memberSince: 'abril de 2024',
      completedRequests: 4,
      hasWhatsApp: true,
    },
  },
  {
    id: 'r2',
    customerName: 'Renata F.',
    customerInitials: 'RF',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 18), // 18 min ago
    distance: 2.9,
    unlockedCount: 2,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 100,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 180,
    estimatedDuration: '1 hora',
    location: {
      city: 'Lapa, São Paulo',
      state: 'SP',
      zipCode: '05041-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Sem pressa, no próximo mês',
      },
      {
        question: 'Qual é o problema?',
        answer: 'Lâmpada queimando com muita frequência no mesmo ponto',
      },
      {
        question: 'Já tentou trocar o soquete?',
        answer: 'Não, tenho medo de mexer',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Só finais de semana',
      },
    ],
    clientInfo: {
      memberSince: 'junho de 2023',
      completedRequests: 14,
      hasWhatsApp: true,
    },
  },
  {
    id: 'r3',
    customerName: 'Gustavo H.',
    customerInitials: 'GH',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: 'Motorola Edge 30 com câmera embaçada e não está focando. Preciso de limpeza interna ou troca do módulo.',
    createdAt: new Date(Date.now() - 1000 * 60 * 35), // 35 min ago
    distance: 5.4,
    unlockedCount: 1,
    maxUnlocks: 4,
    isUrgent: true,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 350,
    attachedImages: 3,
    photos: [
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1080',
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
    ],
    suggestedPrice: 420,
    estimatedDuration: '1-2 dias',
    location: {
      city: 'Santo Amaro, São Paulo',
      state: 'SP',
      zipCode: '04751-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Nos próximos 3 dias',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'Motorola Edge 30',
      },
      {
        question: 'O problema começou após alguma queda?',
        answer: 'Não lembro de ter caído, apareceu do nada',
      },
      {
        question: 'Todas as câmeras estão afetadas?',
        answer: 'Sim, tanto frontal quanto traseira',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Horário comercial',
      },
    ],
    clientInfo: {
      memberSince: 'fevereiro de 2023',
      completedRequests: 22,
      hasWhatsApp: true,
    },
  },
  {
    id: 'r4',
    customerName: 'Larissa C.',
    customerInitials: 'LC',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: 'Trocar válvula de descarga que está vazando e desperdiçando muita água.',
    createdAt: new Date(Date.now() - 1000 * 60 * 50), // 50 min ago
    distance: 3.3,
    unlockedCount: 3,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 200,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=1080',
      'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1080',
    ],
    suggestedPrice: 280,
    estimatedDuration: '1-2 horas',
    location: {
      city: 'Butantã, São Paulo',
      state: 'SP',
      zipCode: '05508-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Em 2 semanas',
      },
      {
        question: 'A válvula já foi comprada?',
        answer: 'Não, preciso que o profissional traga',
      },
      {
        question: 'O vazamento é constante?',
        answer: 'Sim, fica pingando direto',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Tanto faz',
      },
    ],
    clientInfo: {
      memberSince: 'dezembro de 2022',
      completedRequests: 18,
      hasWhatsApp: false,
    },
  },
  {
    id: 'r5',
    customerName: 'Thiago V.',
    customerInitials: 'TV',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 70), // 1h10 ago
    distance: 7.2,
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 150,
    attachedImages: 4,
    photos: [
      'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1080',
      'https://images.unsplash.com/photo-1513828583688-c52646db42da?w=1080',
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1080',
    ],
    suggestedPrice: 390,
    estimatedDuration: '2-3 horas',
    location: {
      city: 'Ipiranga, São Paulo',
      state: 'SP',
      zipCode: '04262-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Próximo mês',
      },
      {
        question: 'Qual é o serviço necessário?',
        answer: 'Instalar tomadas USB na parede',
      },
      {
        question: 'Quantas tomadas USB deseja?',
        answer: '3 pontos (quarto, sala e cozinha)',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não, primeira vez',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Prefiro de tarde',
      },
    ],
    clientInfo: {
      memberSince: 'março de 2024',
      completedRequests: 1,
      hasWhatsApp: true,
    },
  },
  {
    id: 'r6',
    customerName: 'Isabela G.',
    customerInitials: 'IG',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: 'iPhone 11 Pro não reconhece chip. Já tentei resetar e limpar o slot, mas continua sem sinal.',
    createdAt: new Date(Date.now() - 1000 * 60 * 90), // 1h30 ago
    distance: 9.1,
    unlockedCount: 2,
    maxUnlocks: 5,
    isUrgent: true,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 500,
    attachedImages: 5,
    photos: [
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
      'https://images.unsplash.com/photo-1585060544812-6b45742d762f?w=1080',
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1080',
      'https://images.unsplash.com/photo-1610945415295-d9bbf067e59c?w=1080',
    ],
    suggestedPrice: 550,
    estimatedDuration: '1-2 dias',
    location: {
      city: 'Morumbi, São Paulo',
      state: 'SP',
      zipCode: '05650-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Urgente, preciso do celular',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'iPhone 11 Pro',
      },
      {
        question: 'Testou o chip em outro aparelho?',
        answer: 'Sim, o chip funciona em outro celular',
      },
      {
        question: 'O problema começou após alguma atualização?',
        answer: 'Não, começou sem motivo aparente',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Qualquer horário',
      },
    ],
    clientInfo: {
      memberSince: 'janeiro de 2023',
      completedRequests: 7,
      hasWhatsApp: true,
    },
  },
  {
    id: 'r7',
    customerName: 'Lucas M.',
    customerInitials: 'LM',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: 'Instalação de aquecedor a gás no banheiro e ponto de água quente na pia.',
    createdAt: new Date(Date.now() - 1000 * 60 * 110), // 1h50 ago
    distance: 6.8,
    unlockedCount: 1,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: true,
    isViewed: false,
    coinCost: 400,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1080',
      'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1080',
    ],
    suggestedPrice: 850,
    estimatedDuration: '5-6 horas',
    location: {
      city: 'Saúde, São Paulo',
      state: 'SP',
      zipCode: '04144-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Em 3 semanas',
      },
      {
        question: 'O aquecedor já foi comprado?',
        answer: 'Sim, já tenho o aquecedor',
      },
      {
        question: 'Existe ponto de gás próximo?',
        answer: 'Sim, tem ponto de gás no banheiro',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não, primeira vez',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Prefiro de manhã',
      },
    ],
    clientInfo: {
      memberSince: 'maio de 2023',
      completedRequests: 6,
      hasWhatsApp: true,
    },
  },

  // ============================================
  // SECTION 3: OUTROS PEDIDOS (Others)
  // 10 pedidos
  // ============================================
  {
    id: 'o1',
    customerName: 'Amanda S.',
    customerInitials: 'AS',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Instalar campainha sem fio e sensor de presença na entrada.',
    createdAt: new Date(Date.now() - 1000 * 60 * 200), // 3h20 ago
    distance: 11.2,
    unlockedCount: 3,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 1,
    photos: [
      'https://images.unsplash.com/photo-1581092918484-8313de5e6214?w=1080',
    ],
    suggestedPrice: 280,
    estimatedDuration: '2 horas',
    location: {
      city: 'Penha, São Paulo',
      state: 'SP',
      zipCode: '03636-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'No próximo mês',
      },
      {
        question: 'Os equipamentos já foram comprados?',
        answer: 'Sim, já comprei tudo',
      },
      {
        question: 'Precisa fazer furação?',
        answer: 'Sim, na parede externa',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Finais de semana',
      },
    ],
    clientInfo: {
      memberSince: 'setembro de 2024',
      completedRequests: 1,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o2',
    customerName: 'Ricardo P.',
    customerInitials: 'RP',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 230), // 3h50 ago
    distance: 13.5,
    unlockedCount: 1,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 100,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 200,
    estimatedDuration: '1 hora',
    location: {
      city: 'São Miguel, São Paulo',
      state: 'SP',
      zipCode: '08010-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Sem pressa',
      },
      {
        question: 'Qual é o problema?',
        answer: 'Registro de água emperrado',
      },
      {
        question: 'Consegue abrir ou fechar?',
        answer: 'Não gira de jeito nenhum',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Tarde',
      },
    ],
    clientInfo: {
      memberSince: 'julho de 2024',
      completedRequests: 3,
      hasWhatsApp: false,
    },
  },
  {
    id: 'o3',
    customerName: 'Beatriz L.',
    customerInitials: 'BL',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: 'Redmi Note 10 com problema no alto-falante. Som está falhando e chiando.',
    createdAt: new Date(Date.now() - 1000 * 60 * 260), // 4h20 ago
    distance: 15.3,
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 250,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
    ],
    suggestedPrice: 240,
    estimatedDuration: '1 dia',
    location: {
      city: 'Guaianases, São Paulo',
      state: 'SP',
      zipCode: '08460-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Quando possível',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'Redmi Note 10',
      },
      {
        question: 'O problema afeta ligações também?',
        answer: 'Sim, mal consigo ouvir nas chamadas',
      },
      {
        question: 'O celular já tomou água?',
        answer: 'Não que eu saiba',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Qualquer horário',
      },
    ],
    clientInfo: {
      memberSince: 'outubro de 2024',
      completedRequests: 0,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o4',
    customerName: 'Paulo C.',
    customerInitials: 'PC',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Trocar fiação antiga de tomada que está soltando faísca.',
    createdAt: new Date(Date.now() - 1000 * 60 * 290), // 4h50 ago
    distance: 17.8,
    unlockedCount: 2,
    maxUnlocks: 5,
    isUrgent: true,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 300,
    attachedImages: 3,
    photos: [
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
      'https://images.unsplash.com/photo-1513828583688-c52646db42da?w=1080',
      'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1080',
    ],
    suggestedPrice: 420,
    estimatedDuration: '3 horas',
    location: {
      city: 'Itaquera, São Paulo',
      state: 'SP',
      zipCode: '08220-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Urgente, tem risco de incêndio',
      },
      {
        question: 'Quantas tomadas estão afetadas?',
        answer: 'Só uma por enquanto',
      },
      {
        question: 'A casa tem aterramento?',
        answer: 'Não sei',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Quanto antes melhor',
      },
    ],
    clientInfo: {
      memberSince: 'agosto de 2024',
      completedRequests: 2,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o5',
    customerName: 'Fernanda O.',
    customerInitials: 'FO',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: 'Desentupir ralo do box que está acumulando água.',
    createdAt: new Date(Date.now() - 1000 * 60 * 320), // 5h20 ago
    distance: 19.2,
    unlockedCount: 4,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 1,
    photos: [
      'https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=1080',
    ],
    suggestedPrice: 180,
    estimatedDuration: '1 hora',
    location: {
      city: 'Cidade Tiradentes, São Paulo',
      state: 'SP',
      zipCode: '08490-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Esta semana se possível',
      },
      {
        question: 'Já tentou usar desentupidor?',
        answer: 'Sim, mas não resolveu',
      },
      {
        question: 'A água está drenando lentamente ou não drena?',
        answer: 'Drena muito devagar',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Manhã',
      },
    ],
    clientInfo: {
      memberSince: 'junho de 2024',
      completedRequests: 4,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o6',
    customerName: 'Marcos V.',
    customerInitials: 'MV',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: '',
    createdAt: new Date(Date.now() - 1000 * 60 * 350), // 5h50 ago
    distance: 21.5,
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 200,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 150,
    estimatedDuration: '1 hora',
    location: {
      city: 'Jardim Ângela, São Paulo',
      state: 'SP',
      zipCode: '04847-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Quando tiver disponibilidade',
      },
      {
        question: 'Qual é o problema?',
        answer: 'Trocar película de vidro',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'Samsung A54',
      },
      {
        question: 'Prefere película comum ou 3D?',
        answer: 'Comum está bom',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Tanto faz',
      },
    ],
    clientInfo: {
      memberSince: 'novembro de 2024',
      completedRequests: 0,
      hasWhatsApp: false,
    },
  },
  {
    id: 'o7',
    customerName: 'Carla D.',
    customerInitials: 'CD',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Instalação de tomada 220V para ar condicionado.',
    createdAt: new Date(Date.now() - 1000 * 60 * 380), // 6h20 ago
    distance: 24.1,
    unlockedCount: 1,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 250,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1080',
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1080',
    ],
    suggestedPrice: 380,
    estimatedDuration: '2-3 horas',
    location: {
      city: 'Parelheiros, São Paulo',
      state: 'SP',
      zipCode: '04890-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Em 1 mês',
      },
      {
        question: 'O ar condicionado já foi comprado?',
        answer: 'Sim, falta só instalar',
      },
      {
        question: 'A casa tem 220V?',
        answer: 'Sim, mas precisa puxar do quadro',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Sábado',
      },
    ],
    clientInfo: {
      memberSince: 'setembro de 2024',
      completedRequests: 1,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o8',
    customerName: 'Eduardo N.',
    customerInitials: 'EN',
    category: 'Encanador',
    serviceType: 'Presencial',
    description: 'Trocar sifão da pia que está com mau cheiro.',
    createdAt: new Date(Date.now() - 1000 * 60 * 410), // 6h50 ago
    distance: 26.8,
    unlockedCount: 2,
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 100,
    attachedImages: 1,
    photos: [
      'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1080',
    ],
    suggestedPrice: 150,
    estimatedDuration: '1 hora',
    location: {
      city: 'Perus, São Paulo',
      state: 'SP',
      zipCode: '05204-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Próximas semanas',
      },
      {
        question: 'O sifão já foi comprado?',
        answer: 'Não',
      },
      {
        question: 'É pia de cozinha ou banheiro?',
        answer: 'Cozinha',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Sim',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Tarde',
      },
    ],
    clientInfo: {
      memberSince: 'julho de 2024',
      completedRequests: 2,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o9',
    customerName: 'Tatiana B.',
    customerInitials: 'TB',
    category: 'Assistência Técnica para Celular',
    serviceType: 'Presencial',
    description: 'iPhone SE com botão home não funcionando. Preciso trocar ou consertar.',
    createdAt: new Date(Date.now() - 1000 * 60 * 440), // 7h20 ago
    distance: 29.3,
    unlockedCount: 0,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 300,
    attachedImages: 2,
    photos: [
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1080',
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1080',
    ],
    suggestedPrice: 380,
    estimatedDuration: '1 dia',
    location: {
      city: 'Brasilândia, São Paulo',
      state: 'SP',
      zipCode: '02877-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Sem pressa',
      },
      {
        question: 'Qual é o modelo do celular?',
        answer: 'iPhone SE 2020',
      },
      {
        question: 'O Touch ID também não funciona?',
        answer: 'Sim, nada do botão funciona',
      },
      {
        question: 'O botão físico está solto?',
        answer: 'Não, mas não responde quando aperta',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Qualquer horário',
      },
    ],
    clientInfo: {
      memberSince: 'outubro de 2024',
      completedRequests: 0,
      hasWhatsApp: true,
    },
  },
  {
    id: 'o10',
    customerName: 'José A.',
    customerInitials: 'JA',
    category: 'Eletricista',
    serviceType: 'Presencial',
    description: 'Instalar bocal para lâmpada no corredor.',
    createdAt: new Date(Date.now() - 1000 * 60 * 470), // 7h50 ago
    distance: 31.7,
    unlockedCount: 1,
    maxUnlocks: 4,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 100,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 120,
    estimatedDuration: '30 minutos',
    location: {
      city: 'Anhanguera, São Paulo',
      state: 'SP',
      zipCode: '05145-000',
    },
    questionsAndAnswers: [
      {
        question: 'Quando você pretende realizar o serviço?',
        answer: 'Quando possível',
      },
      {
        question: 'A fiação já está puxada?',
        answer: 'Sim, só falta o bocal',
      },
      {
        question: 'Qual tipo de bocal?',
        answer: 'Simples, rosca E27',
      },
      {
        question: 'Você já teve alguma experiência anterior com este tipo de serviço?',
        answer: 'Não',
      },
      {
        question: 'Existe alguma preferência de horário para o profissional comparecer?',
        answer: 'Fim de semana',
      },
    ],
    clientInfo: {
      memberSince: 'novembro de 2024',
      completedRequests: 0,
      hasWhatsApp: false,
    },
  },

  // ============================================
  // TEST SCENARIOS FOR CTA LOGIC
  // 5 pedidos para testar todos os cenários
  // ============================================
  
  // Scenario A: Already unlocked + user has enough coins (500 coins, price 150)
  {
    id: 'scenario-a',
    customerName: 'Teste Cenário A',
    customerInitials: 'TA',
    category: 'Limpeza',
    serviceType: 'Presencial',
    description: 'CENÁRIO A: Pedido já liberado + usuário tem moedas suficientes. Deve mostrar apenas 1 botão azul "Liberar por 150 moedas"',
    createdAt: new Date(Date.now() - 1000 * 60 * 10),
    distance: 2.5,
    neighbourhood: 'Teste',
    unlockedCount: 2, // Already unlocked by 2 pros
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 300,
    estimatedDuration: '1 hora',
    location: {
      city: 'Teste, São Paulo',
      state: 'SP',
      zipCode: '01000-000',
    },
    questionsAndAnswers: [
      {
        question: 'Cenário de teste?',
        answer: 'Sim, cenário A - pedido já liberado + moedas suficientes',
      },
    ],
    clientInfo: {
      memberSince: 'janeiro de 2024',
      completedRequests: 5,
      hasWhatsApp: true,
    },
  },

  // Scenario B: Not unlocked + user has enough for both (500 coins, normal 150, exclusive 250)
  {
    id: 'scenario-b',
    customerName: 'Teste Cenário B',
    customerInitials: 'TB',
    category: 'Jardinagem',
    serviceType: 'Presencial',
    description: 'CENÁRIO B: Ninguém liberou + usuário tem moedas para ambas as opções. Deve mostrar botão verde exclusivo (250) + botão branco normal (150)',
    createdAt: new Date(Date.now() - 1000 * 60 * 15),
    distance: 3.2,
    neighbourhood: 'Teste',
    unlockedCount: 0, // Nobody unlocked yet
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 400,
    estimatedDuration: '2 horas',
    location: {
      city: 'Teste, São Paulo',
      state: 'SP',
      zipCode: '01000-000',
    },
    questionsAndAnswers: [
      {
        question: 'Cenário de teste?',
        answer: 'Sim, cenário B - ninguém liberou + moedas para ambas opções',
      },
    ],
    clientInfo: {
      memberSince: 'fevereiro de 2024',
      completedRequests: 8,
      hasWhatsApp: true,
    },
  },

  // Scenario C: Already unlocked + NOT enough coins (20 coins, price 150)
  {
    id: 'scenario-c',
    customerName: 'Teste Cenário C',
    customerInitials: 'TC',
    category: 'Pintura',
    serviceType: 'Presencial',
    description: 'CENÁRIO C: Pedido já liberado + usuário NÃO tem moedas suficientes. Deve mostrar apenas 1 botão azul "Liberar por 150 moedas (completar saldo)"',
    createdAt: new Date(Date.now() - 1000 * 60 * 20),
    distance: 4.1,
    neighbourhood: 'Teste',
    unlockedCount: 3, // Already unlocked by 3 pros
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 500,
    estimatedDuration: '3 horas',
    location: {
      city: 'Teste, São Paulo',
      state: 'SP',
      zipCode: '01000-000',
    },
    questionsAndAnswers: [
      {
        question: 'Cenário de teste?',
        answer: 'Sim, cenário C - pedido já liberado + SEM moedas',
      },
    ],
    clientInfo: {
      memberSince: 'março de 2024',
      completedRequests: 12,
      hasWhatsApp: true,
    },
  },

  // Scenario D: Not unlocked + enough for normal but NOT exclusive (200 coins, normal 150, exclusive 250)
  {
    id: 'scenario-d',
    customerName: 'Teste Cenário D',
    customerInitials: 'TD',
    category: 'Marcenaria',
    serviceType: 'Presencial',
    description: 'CENÁRIO D: Ninguém liberou + usuário tem moedas para normal mas NÃO para exclusivo. Deve mostrar botão verde exclusivo com "(completar saldo)" + botão branco normal SEM "(completar saldo)"',
    createdAt: new Date(Date.now() - 1000 * 60 * 25),
    distance: 5.5,
    neighbourhood: 'Teste',
    unlockedCount: 0, // Nobody unlocked yet
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 600,
    estimatedDuration: '4 horas',
    location: {
      city: 'Teste, São Paulo',
      state: 'SP',
      zipCode: '01000-000',
    },
    questionsAndAnswers: [
      {
        question: 'Cenário de teste?',
        answer: 'Sim, cenário D - ninguém liberou + moedas só para normal',
      },
    ],
    clientInfo: {
      memberSince: 'abril de 2024',
      completedRequests: 15,
      hasWhatsApp: true,
    },
  },

  // Scenario E: Not unlocked + NOT enough for either (10 coins, normal 150, exclusive 250)
  {
    id: 'scenario-e',
    customerName: 'Teste Cenário E',
    customerInitials: 'TE',
    category: 'Serralheria',
    serviceType: 'Presencial',
    description: 'CENÁRIO E: Ninguém liberou + usuário NÃO tem moedas para nenhuma opção. Deve mostrar AMBOS os botões COM "(completar saldo)"',
    createdAt: new Date(Date.now() - 1000 * 60 * 30),
    distance: 6.8,
    neighbourhood: 'Teste',
    unlockedCount: 0, // Nobody unlocked yet
    maxUnlocks: 5,
    isUrgent: false,
    isHighlighted: false,
    isRecommended: false,
    isViewed: false,
    coinCost: 150,
    attachedImages: 0,
    photos: [],
    suggestedPrice: 700,
    estimatedDuration: '5 horas',
    location: {
      city: 'Teste, São Paulo',
      state: 'SP',
      zipCode: '01000-000',
    },
    questionsAndAnswers: [
      {
        question: 'Cenário de teste?',
        answer: 'Sim, cenário E - ninguém liberou + SEM moedas para nenhuma opção',
      },
    ],
    clientInfo: {
      memberSince: 'maio de 2024',
      completedRequests: 20,
      hasWhatsApp: true,
    },
  },
];